/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { ClusterService, LbErrorService, PulseHAApiService, SIPApiService, SipModel } from '../../../shared';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LanguageService, LoadingService, LogService, RbacService, UtilsService } from '../../../../../core/services';
import { PulseHAGlobalModel } from '../../../shared/models/pulseHAGlobal.model';
import { PulseHAModel } from '../../../shared/models/pulseHAModel';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { portRangeValidation } from '../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ha-conf',
    templateUrl: './pulseConf.component.html',
    styleUrls: ['./pulseConf.component.scss']
})

export class PulseConfComponent extends BaseComponent {
    /**
     * Define our form group.
     */
    public pulseHAGlobalForm: FormGroup;

    /**
     * Define our form group.
     */
    public pulseHAForm: FormGroup;

    /**
     * If nodes is paired.
     */
    public paired: boolean = false;

    /**
     * Store dropdown content.
     */
    public dropdown: any = {
        sips: [],
        logLevels: []
    };

    /**
     * Store button permissions.
     */
    public permissionsLookup: any = {};

    /**
     * To store the initial pulse HA global form data.
     * @private
     */
    private initialPulseHAGlobalFormData: any = {};

    /**
     * To store the initial pulse HA form data.
     * @private
     */
    private initialPulseHAFormData: any = {};

    /**
     * Store the changed forms.
     * @private
     */
    private changedForms = {};

    constructor(
        private clusterService: ClusterService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private pulseHAService: PulseHAApiService,
        private rbacService: RbacService,
        private sipApiService: SIPApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpForms();
        this.setUpFormSubscribers();
        this.setUpPageData();
        this.subscribeToClusterService();
        this.registerButtons();
    }

    /**
     * A check to determine if the button is disabled or not.
     */
    public isButtonDisabled(): boolean {
        return (
            (this.pulseHAGlobalForm.invalid || this.pulseHAGlobalForm.pristine) &&
            (this.pulseHAForm.invalid || this.pulseHAForm.pristine)
        );
    }

    /**
     * Group submit function.
     */
    public submit(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiCalls: any = {};
            if (this.changedForms['pulseHAGlobalForm']) {
                apiCalls['pulseHAGlobalForm'] = this.submitPulseHAGlobal();
            }

            if (this.changedForms['pulseHAForm']) {
                apiCalls['pulseHAForm'] = this.submitPulseHA();
            }

            forkJoin(apiCalls)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('PhysicalAdvConfComponent submitForms ', error.message);
                        observer.error();
                    },
                    complete: (): void => {
                        this.changedForms = {};
                        this.loadingService.loadingButtonEmitter.next(false);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Submit our changes via the api
     */
    public submitPulseHAGlobal(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const pulseGlobal: PulseHAGlobalModel = <PulseHAGlobalModel>this.pulseHAGlobalForm.value;
            this.pulseHAService.updateConfigGlobal(pulseGlobal)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.pulseHAGlobalForm, error.error.errors);
                        this.logService.error('PulseConfComponent: submitPulseHAGlobal', 'Error submitting data', error);
                        observer.error(error);
                    },
                })
                .add((): void => {
                    observer.complete();
                });
        });
    }

    /**
     * Submit our changes via the api.
     */
    public submitPulseHA(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const pulseHa: PulseHAModel = <PulseHAModel>this.pulseHAForm.value;
            this.pulseHAService.updateConfig(pulseHa)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.pulseHAForm, error.error.errors);
                        this.logService.error('PulseConfComponent: submitPulseHA', 'Error submitting data', error);
                        observer.error(error);
                    }
                })
                .add((): void => {
                    observer.complete();
                });
        });
    }

    /**
     * Set up forms.
     * @private
     */
    private setUpForms(): void {
        this.pulseHAGlobalForm = this.formBuilder.group({
            hcInterval: ['', Validators.required],
            foInterval: ['', Validators.required],
            foLimit: ['', Validators.required],
            logLevel: ['', Validators.required],
            autoFailBack: [false]
        });

        this.pulseHAForm = this.formBuilder.group({
            sipId: ['', Validators.required],
            port: ['', [Validators.required, portRangeValidation]]
        });
    }

    /**
     * Subscribe to the default gateway form changes.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.pulseHAGlobalForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('pulseHAGlobal', () => {
                        const formHasChanged: boolean = this.utilsService.hasFormDataChanged(this.initialPulseHAGlobalFormData, form);
                        this.changedForms['pulseHAGlobalForm'] = formHasChanged;

                        this.validateIntervalValues('hcInterval', form['hcInterval']);
                        this.validateIntervalValues('foInterval', form['foInterval']);
                        this.validateIntervalValues('foLimit', form['foLimit']);

                        if (!formHasChanged) {
                            this.pulseHAGlobalForm.markAsPristine();
                        } else {
                            this.pulseHAGlobalForm.markAsDirty();
                        }
                    });
                }
            });

        this.pulseHAForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('pulseHA', (): void => {
                        const formHasChanged = this.utilsService.hasFormDataChanged(this.initialPulseHAFormData, form);
                        this.changedForms['pulseHAForm'] = formHasChanged;
                        if (!formHasChanged) {
                            this.pulseHAForm.markAsPristine();
                        } else {
                            this.pulseHAForm.markAsDirty();
                        }
                    });
                }
            });
    }

    /**
     * Validate intervals and limits values.
     * @param formField
     * @param formValue
     * @private
     */
    private validateIntervalValues(formField: string, formValue: number): void {
        let invalid: boolean;
        let message: string;
        switch (formField) {
            case 'hcInterval':
            case 'foInterval':
                invalid = formValue < 1000 || formValue > 10000;
                message = 'pulse_conf_interval';
                break;
            case 'foLimit':
                invalid = formValue < 2000 || formValue > 10000;
                message = 'pulse_conf_fail_over_limit';
                break;
            default:
                break;
        }

        if (invalid) {
            this.pulseHAGlobalForm.get(formField).setErrors({ 'invalid': this.languageService.translate(message) });
        } else {
            this.pulseHAGlobalForm.get(formField).setErrors(null);
        }
    }

    /**
     * Set up data for the page.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            configGlobal: this.pulseHAService.getConfigGlobal(),
            config: this.pulseHAService.getConfig(),
            sips: this.sipApiService.getStaticIPs(),
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.dropdown.sips = this.getSipDropdownContent(data['sips']);
                    this.dropdown.logLevels = this.getLogLevels();
                    this.patchDataToForm(data['configGlobal'], data['config']);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PulseConfComponent: setUpPageData', error.message);
                }
            });
    }

    /**
     * Convert the ips to dropdown model to provide content to the ip dropdown.
     * @private
     */
    private getSipDropdownContent(sips: SipModel[]): DropdownModel[] {
        const dropdownContent: DropdownModel[] = [];
        for (const sip of sips) {
            dropdownContent.push(<DropdownModel>{ key: sip.id, value: sip.ip });
        }

        return dropdownContent;
    }

    /**
     * Dropdown model to provide content to the log level dropdown.
     * @private
     */
    private getLogLevels(): DropdownModel[] {
        return [
            <DropdownModel>{ key: 'info', value: 'Info' },
            <DropdownModel>{ key: 'debug', value: 'Debug' }
        ];
    }

    /**
     * Set paired status.
     * @private
     */
    private getPairedStatus(): boolean {
        const paired: boolean = this.clusterService.isNodeInCluster();
        if (paired) {
            this.pulseHAForm.disable();
        }

        return paired;
    }

    /**
     * Patch data to form.
     * @private
     */
    private patchDataToForm(pulseConfigGlobal: any, pulseConfig: any): void {
        this.pulseHAGlobalForm.patchValue(pulseConfigGlobal);
        this.pulseHAForm.patchValue(pulseConfig);

        this.initialPulseHAGlobalFormData = pulseConfigGlobal;
        this.initialPulseHAFormData = pulseConfig;
    }

    /**
     * Create our emitter handlers.
     * @private
     */
    private subscribeToClusterService(): void {
        // Get initial values.
        this.paired = this.getPairedStatus();
        // Subscribe to HA status changes
        this.clusterService.nodesEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe((): void => {
                this.paired = this.getPairedStatus();
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'pulse-ha-global': [{ path: '/pulseha/globals', methods: ['PUT'] }],
            'pulse-ha': [{ path: '/pulseha', methods: ['PUT'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['pulse-ha']) {
            this.pulseHAForm?.disable();
        }

        if (!this.permissionsLookup['pulse-ha-global']) {
            this.pulseHAGlobalForm?.disable();
        }
    }
}
